import { v4 as uuidV4 } from "uuid";
import dataset from "../../lib/datasets/manifolds";
import mainDataset from "../../lib/datasets/dataset";
import nextId from "react-id-generator";
import { ManifoldData, ManifoldNodes } from "../project-build";
import _ from "lodash";
import { CHAMBER_TYPE_ID_SC160LP, CHAMBER_TYPE_ID_SC310, INLET_MANIFOLD_ID } from "../types";
import { ManifoldStructureType } from "../manifolds";

export const ManifoldUtils = {
  /**
   * Returns the flow rate for the direct pipe connection of an outlet manifold based on the provided size of the direct pipe connection
   * @param {number} directPipeSize
   */
  generateManifoldId: () => {
    return uuidV4();
  },

  /**
   * Returns an array containing all row indexes that the provided manifold touches.
   * @param {Manifold} manifold The manifold to locate row index points for.
   * @return {*}
   */
  getRowsOccupiedByManifold: ( manifold ) => {
    let end;
    // UPPERS
    if (( manifold.direction === 2 && manifold.isUpper )) {
      end = manifold.letRow + ( manifold.stubsNumber * manifold.spacer ) + ( manifold.spacer === 2 ? 0 : 1 );
      let range;
      if (manifold._spacer && manifold._spacer >= 2) { // spacer
        range = _.range(manifold.letRow + 1, end, 1); //Ok
      } else { // no  spacer
        range = _.range(manifold.letRow + 1, end, 1); //OK
      }
      const ret = [manifold.letRow].concat(range);
      return [...new Set(ret)];
    }
    if (( manifold.direction === 1 && manifold.isUpper )) {
      end = manifold.letRow - ( manifold.stubsNumber * manifold.spacer ) - ( manifold.spacer === 2 ? 0 : 1 ) + ( manifold.manifoldSize.ChamberTypeId === CHAMBER_TYPE_ID_SC160LP ? 0 : 1 );
      let range;
      if (manifold._spacer && manifold._spacer >= 2) { // spacer
        range = _.range(manifold.letRow, end - 1, -1);
      } else { // no spacer
        range = _.range(manifold.letRow, end - 1, -1);
      }
      const ret = [manifold.letRow].concat(range);
      return [...new Set(ret)];
    }

    // LOWERS
    if (( manifold.direction === 1 && !manifold.isUpper )) { // Le & Up
      end = manifold.letRow - ( manifold.stubsNumber * manifold.spacer ) - ( manifold.spacer === 2 ? 0 : 1 ) + ( manifold.manifoldSize.ChamberTypeId === CHAMBER_TYPE_ID_SC160LP ? 0 : 1 );
      let range;
      if (manifold._spacer && manifold._spacer >= 2) { // spacer
        range = _.range(manifold.letRow, end - 1, -1);
      } else { // no spacer
        range = _.range(manifold.letRow, end - 1, -1);
      }
      const ret = [manifold.letRow].concat(range);
      return [...new Set(ret)];

    }
    if (( manifold.direction === 2 && !manifold.isUpper )) { // Le & Lo
      end = manifold.letRow + ( manifold.stubsNumber * manifold.spacer ) + ( manifold.spacer === 2 ? 0 : 1 );
      let range;
      if (manifold._spacer && manifold._spacer >= 2) { // spacer
        range = _.range(manifold.letRow + 1, end, 1); //Ok
      } else { // no  spacer
        range = _.range(manifold.letRow + 1, end, 1); //OK
      }
      const ret = [manifold.letRow].concat(range);
      return [...new Set(ret)];
    }
  },

  /**
   * get the data structure from the dataset based on ID
   * @returns {*} -  Camber Object
   * @param chamberId {string} - Chamber ID, obtained from main dataset
   */
  getDatasetByChamberId: ( chamberId ) => {
    return dataset.byChambers.filter(chamber => chamber.id === chamberId)[0];
  },

  /**
   * Returns CFS For Outlet Dataset By Size
   * @param size
   * @returns {number}
   */
  getCFSForOutletDatasetBySize: ( size ) => {
    try {
      return dataset["outlet-values"][size.toString()];
    } catch (e) {
      console.error(`ManifoldUtils.getCFSForOutletDatasetBySize() ::size is invalid or undefined`);
      return 0;
    }
  },

  /**
   * Returns Manifold Sizing By Chamber Type
   * @param {chamberType} chamberType
   * @param {volume} volume
   * @returns {*} manifold size by volume object
   */
  getManifoldSizingByChamberType: ( chamberType, volume ) => {
    try {
      return dataset["manifold-sizing-by-volume"][chamberType].reduce(( last, manifoldSizing ) => {
        return manifoldSizing.low <= volume ? manifoldSizing : last;
      }, null);
    } catch (e) {
      console.error("ManifoldUtils.getManifoldSizingByChamberType() :: chamberType is invalid or undefined");
      return null;
    }
  },

  /**
   * Returns the manifold dataset arranged as key text object
   * @param manifoldArray
   * @param unit
   * @returns {*}
   */
  getManifoldDatasetForUIArray: ( manifoldArray, unit = "imperial" ) => {
    return manifoldArray.map(( data ) => {
      let textValue = data["size"];
      const valueArray = data["size"].toLowerCase().split("x");
      // if metric
      if (unit !== "imperial") textValue = `${valueArray[0] * 25}X${valueArray[1] * 25}`;
      return { key: `${data["size"]}`, text: `${textValue}`, sizeData: { ...data } };
    });
  },

  /**
   * Returns the nominal size from the size string 'nXn'
   * @param size
   * @param type
   * @returns {string|null}
   */
  getManifoldNominals: ( size, type = "trunk" ) => {
    try {
      const trunkStubArray = size.toLowerCase().split("x");
      return type === "trunk" ? trunkStubArray[0] : trunkStubArray[1];
    } catch (e) {
      console.error(`getManifoldNominals::size should be "nXn", is invalid or undefined`);
      return null;
    }
  },

  getManifoldMetadataByTypeAndSize: ( type, size ) => {
    try {
      return dataset["manifold-metadata"][type].find(m => m.innerDiameter === size);
    } catch (e) {
      console.error("Could not retrieve manifold metadata for type and size:", { type, size });
      return null;
    }
  },

  /**
   * get the data structure from the dataset based on ID
   * @returns {*} -  Camber Object
   * @param chamberId {string} - Chamber ID, obtained from main dataset
   */
  getMainDatasetByChamberId: ( chamberId ) => {
    return mainDataset.elements.filter(chamber => chamber.id === chamberId)[0];
  },

  /**
   * Returns the pipeSize values for a requested size
   * @param chamberObject {object} -
   * @param pipeSize {string} - as '6X6'
   * @param position {string} - as low or high
   * @returns {*}
   */
  getDatasetValuesByPipeSizeStubInverts: ( chamberObject, pipeSize, position = "low" ) => {
    try {
      const obj = chamberObject["pipeSize-stub-inverts"]
        .filter(pipe => pipe.size === pipeSize.toLowerCase().split("x")[1])[0];
      return ( position === "low" )
        ? parseFloat(obj.low)
        : parseFloat(obj.high);
    } catch (e) {
      return 0;
    }
  },

  /**
   * Returns high low boolean values
   * @param manifold
   * @param requestStructureObj
   * @returns {{isLow: boolean, isHigh: boolean}}
   */
  getManifoldHighOrLow: ( manifold, requestStructureObj ) => {
    if (requestStructureObj.state.chamberRows[manifold._letRow].chambers[manifold._letColumn]._isIsolatorArray.indexOf(manifold.id) === -1
      || requestStructureObj.chamberTypeId === CHAMBER_TYPE_ID_SC160LP) {
      return {
        isHigh: false,
        isLow: true
      };
    } else if (manifold._assembly.find(assm => assm._structureType === 2)) {
      return {
        isHigh: true,
        isLow: false
      };
    } else {
      return {
        isHigh: manifold._isElevated,
        isLow: manifold._hasWeir
      };
    }
  },

  /**
   * Calculates the Outlet Flow based on the following calculation
   * OCS Outflow Total = DirectStub + Min( trunk, (stubFlow * numberOfStubs) )
   * Take all values from getCFSForOutletDatasetBySize(size)
   * @param manifoldSize {string}
   * @param isolatorStubSize {number}
   * @param stubsNumber {number}
   * @returns {number}
   */
  calculateOutletManifoldFlowRate: ( manifoldSize, isolatorStubSize, stubsNumber ) => {
    const trunkSize = parseInt(manifoldSize.toLowerCase().split("x")[0], 10);
    const stubSize = parseInt(manifoldSize.toLowerCase().split("x")[1], 10);
    const trunkOutletSize = ManifoldUtils.getCFSForOutletDatasetBySize(trunkSize);
    const stubOutletSize = ManifoldUtils.getCFSForOutletDatasetBySize(stubSize);
    // return isoStubSize + Math.min(trunkOutletSize, stubOutletSize * stubsNumber);
    // changes to use stubSize as requested by Jamie
    return stubOutletSize + Math.min(trunkOutletSize, stubOutletSize * stubsNumber);
  },

  /**
   * Returns flow rates based on manifold and position
   * @param manifold {object} -  manifold object
   * @param isHigh {boolean} - manifold position, high or low
   * @param isIsolator
   * @returns {*}
   */
  getTotalFlowRate: ( manifold, isHigh, isIsolator ) => {

    const stubFlowRate = isHigh
      ? manifold._manifoldSize.highFlowRate
      : manifold._manifoldSize.lowFlowRate;

    const trunkMaxFlowRate = isHigh
      ? manifold._manifoldSize.highTrunkMaxFlowRate
      : manifold._manifoldSize.lowTrunkMaxFlowRate;

    const calcFlowRate = Math.round(( manifold._stubsNumber * stubFlowRate ) * 100) / 100;

    const inletRate = calcFlowRate > trunkMaxFlowRate ? trunkMaxFlowRate : calcFlowRate;

    const outletRate = ManifoldUtils.calculateOutletManifoldFlowRate(
      manifold.manifoldSize.size,
      manifold.manifoldSize.isolatorStubSize,
      manifold._stubsNumber
    );
    return manifold._isInlet
      ? inletRate
      : outletRate;
  },

  isSizeOnSize: ( size ) => {
    try {
      const numbersArray = size.toLowerCase().split("x");
      return numbersArray[0] === numbersArray[1];
    } catch (e) {
      console.error(`isSizeOnSize::size should be "nXn", is invalid or undefined : ${e.message}`);
      return false;
    }
  },

  /**
   * Builds the payload array that will be sent to the backend
   * @param manifolds {[]}
   * @param requestStructureObj {object}
   * @returns {[]}
   */
  buildManifoldsPayloadArray: ( manifolds, requestStructureObj ) => {
    const returnArray = [];
    const dataset = ManifoldUtils.getDatasetByChamberId(requestStructureObj.chamberTypeId);
    try {
      if (dataset === undefined) throw new Error("getDatasetByChamberId could not find the dataset");
      manifolds["manifolds"]
        .forEach(( manifold ) => {
          const isUpper = manifold._isUpper;
          const { isHigh } = ManifoldUtils.getManifoldHighOrLow(manifold, requestStructureObj);

          const manifoldDatasetByChamberIdArray = ManifoldUtils.getManifoldDatasetByChamberId(requestStructureObj.chamberTypeId, isHigh ? "high" : "low");
          const manifoldDatasetObj = ManifoldUtils.getManifoldDatasetObject(manifoldDatasetByChamberIdArray, manifold._manifoldSize.size);

          // TODO: Run the flow rate calc here passing in the manifoldSize object and the number of stubs:
          // i.e. calculateManifoldFlowRate(manifold._numberOfStubs, etc.) (need all sizing data)
          const ttlFlowRate = ManifoldUtils.getTotalFlowRate(manifold, isHigh, manifold.hasIsolator);
          const manifoldObj = {
            id: nextId("manifold-id-"),
            instanceType: manifold._isInlet ? "Inlet" : "Outlet",
            style: "Single Manifold",                                   // Always 'Single Manifold' for now
            originNode: `R${manifold._letRow}C${manifold._letColumn}`,  // where it connects
            manifoldSelectionDirect: { ...manifold._manifoldSize },            // dialog
            totalManifoldFlowRate: ttlFlowRate,                            // Needs calculated (see note above)
            chamberType: dataset["id"],                                 // Selected Chamber
            rowSpacing: ( manifold._spacer === 2 ),    // "Stubs every other" True or False
            skipRows: ( manifold._spacer === 2 ),    // Same as rowSpacing ^^^
            rowGap: requestStructureObj.state.rowSpacing * 12, // Same as Row Spacing on Advanced settings -chamberRowSpacing-
            adsUnits: requestStructureObj.state.adsUnits,
            perimeterStoneOffset: requestStructureObj.state.perimeterStoneOffset, // Same as Perimeter Stone Offset on Advanced settings -perimeterStoneOffset-
            manifoldType: isUpper ? "upper" : "lower",
            legacySizingMethod: "MiniCAD",
            stubCount: manifold._numberOfStubs
          };

          if (!manifold._isInlet) {
            manifoldObj.manifoldSelectionDirect.isolatorStubSize = manifold._manifoldSize.stubNominal;
          }

          const manifoldX = new ManifoldData(manifoldObj);

          manifold._assembly.forEach(( manifoldAssembly, idx ) => {
            const hasBasin = ( manifoldAssembly.structureType !== null );
            const hasSecondDirectPipe = hasBasin && manifold._hasSecondPipe;
            let angleArray = [];
            let structureType = null;

            // Detect pipe type
            switch (manifoldAssembly.manifoldType) {
              case 0: // LeftElbow
                angleArray = isUpper
                  ? hasSecondDirectPipe ? [0, 90, 180] : [0, 90]
                  : hasSecondDirectPipe ? [0, 180, 270] : [0, 270];
                break;
              case 1: // RightElbow
                angleArray = isUpper
                  ? hasSecondDirectPipe ? [0, 180, 270] : [0, 270]
                  : hasSecondDirectPipe ? [0, 90, 180] : [0, 90];
                break;
              case 2: // StraightHorizontalPipe
                angleArray = isUpper
                  ? hasSecondDirectPipe ? [90, 180, 270] : [90, 270]
                  : hasSecondDirectPipe ? [90, 180, 270] : [90, 270];
                break;
              case 3: // StraightVerticalPipe
                angleArray = isUpper
                  ? hasSecondDirectPipe ? [0, 180] : [0]
                  : hasSecondDirectPipe ? [0, 180] : [0];
                break;
              case 4: // TeePipe
                angleArray = isUpper
                  ? hasSecondDirectPipe ? [0, 90, 180, 270] : [0, 90, 270]
                  : hasSecondDirectPipe ? [0, 90, 180, 270] : [0, 90, 270];
                break;
              default:
              //none
            }

            // Set basin type
            switch (manifoldAssembly.structureType) {
              case 0:
                structureType = "Round Concrete";
                break;
              case 1:
                structureType = "Square Concrete";
                break;
              case 2:
                structureType = "Nyloplast";
                break;
              default:
                break;
            }

            const col = ( isUpper ) ? manifold._letColumn - 1 : manifold._letColumn + 1;

            let isEccentric, isConcentric;
            if (ManifoldUtils.isSizeOnSize(manifold._manifoldSize.size)) {
              isConcentric = false;
              isEccentric = false;
            } else { // Reducing Tee
              if (requestStructureObj.chamberTypeId === "SC-160LP") {
                isConcentric = true;
                isEccentric = false;
              } else {
                isConcentric = false;
                isEccentric = true;
              }
            }

            const nodeObj = {
              nodeId: `R${manifoldAssembly.rowIndex}C${col}`,
              angles: angleArray,
              trunkNominalSize: ManifoldUtils.getManifoldNominals(manifold._manifoldSize.size, "trunk"),
              stubNominalSize: ManifoldUtils.getManifoldNominals(manifold._manifoldSize.size, "stub"),
              trunkInvert: 0,                              // Should be 0 always
              trunkFlowRate: manifold._manifoldSize[( isHigh ? "highTrunkMaxFlowRate" : "lowTrunkMaxFlowRate" )],
              stubInvertPosition: isHigh ? "High" : "Low",
              stubInvert: ManifoldUtils.getDatasetValuesByPipeSizeStubInverts(
                dataset,
                manifold._manifoldSize.size,
                isHigh ? "high" : "low"
              ),
              stubFlowRate: manifold._manifoldSize[( isHigh ? "highFlowRate" : "lowFlowRate" )],
              isEccentric,
              isConcentric,
              isBasin: manifoldAssembly.structureType !== null,
              trunkMaxCfs: manifold._manifoldSize[( isHigh ? "highTrunkMaxFlowRate" : "lowTrunkMaxFlowRate" )],
              isoDirStubMaxCfs: 0                   // We're not sending this yet - SKIP for now
            };

            const manifoldNode = new ManifoldNodes(nodeObj);

            // Detect Basin and save metadata
            if (hasBasin) {
              let basinValuesObj = {
                nominalSize: structureType === "Nyloplast" ? 30 : 48,
                minimalDistance: 12
              };
              // If I get the manifoldObj I will reaign the data based on manifold.json, if not I will use
              // fixed data as fallback
              if (manifoldDatasetObj) {
                basinValuesObj = {
                  nominalSize: structureType === "Nyloplast" ? manifoldDatasetObj.nyloplastStructureSize : manifoldDatasetObj.concreteStructureSize,
                  minimalDistance: 12
                };
              }

              let manifoldDir = null;
              if (manifold._isManifoldInner && manifold.isOutlet && requestStructureObj.state.isUnderDrainEnabled) {
                const perimeterRow = requestStructureObj.state.chamberRows[manifold._letRow];
                const firstActive = perimeterRow.getFirstActiveChamber().col;
                const lastActive = perimeterRow.getLastActiveChamber().col;
                let upperChambers = ( manifold.letColumn + ( isUpper ? -1 : 1 ) ) - firstActive;
                let lowerChambers = lastActive - ( manifold.letColumn + ( isUpper ? -1 : 1 ) );
                manifoldDir = upperChambers > lowerChambers
                  ? "UP"
                  : "DOWN";
              }

              manifoldNode.setBasinMetadata({
                basinType: structureType,
                nominalSize: basinValuesObj.nominalSize,
                directStubSize: manifold.hasIsolator ? manifold._manifoldSize.isolatorStubSize : parseInt(manifoldNode.data.stubNominalSize, 10),
                bypassMethod: manifold._hasWeir
                  ? "weir"
                  : manifold._hasElevatedManifold
                    ? "elevated manifold"
                    : null,
                eccentricTeeTrunkSize: 0,               // (Will be number / Always 0)
                eccentricTeeStubSize: 0,                // (Will be number / Always 0)
                outletMethod: "Structure",              // Always Structure
                underDrainDirection: "Auto",            // Always Auto
                underDrainPosition: "Auto",             // Always Auto
                underdrainDirMCD: manifoldDir,
                // isUnderDrainEnabled: requestStructureObj.state.isUnderDrainEnabled,
                isUnderDrainEnabled: false,
                underDrainSize: [CHAMBER_TYPE_ID_SC160LP, CHAMBER_TYPE_ID_SC310].indexOf(dataset["id"]) > -1 ? 4 : 6,
                minimalDistance: basinValuesObj.minimalDistance
              });
            }
            manifoldX.addNode(manifoldNode.returnPlainObject());
          });
          returnArray.push(manifoldX.returnPlainObject());
        });
    } catch (error) {
      console.error(`ManifoldUtils.buildManifoldsPayloadArray() :: ${error.message}`);
    }
    return returnArray;
  },


  getAllManifoldPipeSizeData: () => {
    try {
      return dataset["manifold-pipe-sizes"];
    } catch (e) {
      console.error("getAllManifoldPipeSizeData could not be found in the dataset.");
      return null;
    }
  },

  getManifoldPipeSizeData: ( pipeSize ) => {
    try {
      return dataset["manifold-pipe-sizes"].find(ps => ps.pipeSize === pipeSize);
    } catch (e) {
      console.error("getManifoldPipeSizeData::pipeSize is invalid or undefined");
      return null;
    }
  },

  getManifoldStructureDimensions: ( structureType ) => {
    try {
      return dataset["manifold-structures"][structureType];
    } catch (e) {
      console.error("getManifoldStructureDimensions could not retrieve structure dimensions for structure " + structureType);
      return null;
    }
  },

  getManifoldSeparation: ( manifold, chamberType, chamberRowSpacing, separationDistance, isIsolator ) => {
    debugger;
    //  TODO this function appears not to be used any more, should be removed, so the debugger is used to validate
    // its not  in use nay more, added on  [06 may 2021], should be safely removed after  [06 jul 2021]
    let manifoldSeparations = {
      trunkSeparation: 0,
      bendSeparation: 0,
      endCapSeparation: 0
    };

    try {
      manifoldSeparations.trunkSeparation = ManifoldUtils.getTrunkSeparation(manifold, separationDistance, isIsolator);

      manifoldSeparations.bendSeparation = getManifoldBendSeparation(manifold, chamberType, chamberRowSpacing, separationDistance, isIsolator);

      manifoldSeparations.endCapSeparation = getManifoldEndCapSeparation(manifold, separationDistance, isIsolator);
    } catch (exception) {
      console.warn("An issue occurred while calculating manifold separations");
    }

    return manifoldSeparations;
  },
  getManifoldSeparationBySize: ( manifoldSizeByVolume, chamberType, chamberRowSpacing, separationDistance, isIsolator ) => {
    let inletManifoldSeparations = {
      trunkSeparation: 0,
      bendSeparation: 0,
      endCapSeparation: 0
    };
    let outletManifoldSeparations = {
      trunkSeparation: 0,
      bendSeparation: 0,
      endCapSeparation: 0
    };

    try {
      const trunkSeparations = ManifoldUtils.getTrunkSeparationBySize(manifoldSizeByVolume, chamberType, separationDistance, isIsolator);
      inletManifoldSeparations.trunkSeparation = trunkSeparations.inletTrunkSeparation;
      outletManifoldSeparations.trunkSeparation = trunkSeparations.outletTrunkSeparation;

      const bendSeparations = getManifoldBendSeparationBySize(manifoldSizeByVolume, chamberType, chamberRowSpacing, separationDistance, isIsolator);
      inletManifoldSeparations.bendSeparation = bendSeparations.inletBendSeparation;
      outletManifoldSeparations.bendSeparation = bendSeparations.outletBendSeparation;

      const endCapSeparations = getManifoldEndCapSeparationBySize(manifoldSizeByVolume, chamberType, separationDistance, isIsolator);
      inletManifoldSeparations.endCapSeparation = endCapSeparations.inletEndCapSeparation;
      outletManifoldSeparations.endCapSeparation = endCapSeparations.outletEndCapSeparation;
    } catch (exception) {
      console.warn("An issue occurred while calculating manifold separations");
    }

    return { inletManifoldSeparations, outletManifoldSeparations };
  },

  getTrunkSeparation: ( manifold, separationDistance, isIsolator, preventCollapse = false ) => {
    try {
      let isolatorStubSize = manifold.manifoldSize.isolatorStubSize;
      if (manifold.isInlet && isIsolator && !manifold.manifoldSize.isolatorStubSize) {
        //update the isolator stub size if it's missing here
        const matchingSize = dataset["by-chamber"][manifold.manifoldSize.ChamberTypeId].find(bc => bc.size === manifold.manifoldSize.size);
        if (matchingSize && matchingSize.isolatorStubSize) {
          isolatorStubSize = parseInt(matchingSize.isolatorStubSize, 10);
        } else {
          isolatorStubSize = 0;
        }
      }

      const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();
      let endCapPipeSize = manifoldPipeSizes.find(
        size => size.pipeSize === ( ( isIsolator && manifold.isInlet ) ? isolatorStubSize : manifold.manifoldSize.stubNominal )
      );

      let trunkPipeSize = manifoldPipeSizes.find(
        size => size.pipeSize === manifold.manifoldSize.trunkNominal
      );

      const isNylo = !!( manifold.assembly.find(ma => ma.structureType === 2) );
      let structureSize = manifold.manifoldSize[isNylo ? "nyloplastStructureSize" : "concreteStructureSize"];
      const structureType = isNylo ? "nyloplast" : manifold.assembly.find(ma => ma.structureType === 0) ? "round" : "square";

      if (!structureSize) {
        structureSize = 48;
      }

      let initialSeparation = 1; // (12") //manifoldSizing.structureDistance;
      let adapterSeparation = 0;
      let pipeSeparation =
        structureSize / 12 / 2 +
        trunkPipeSize.pipeOD / 2 +
        trunkPipeSize.stonePerimeter;
      //initialSeparation +
      //trunkPipeSize.stonePerimeter;

      if (manifold.assembly.length < 2 && !preventCollapse) {
        pipeSeparation = ( separationDistance - 12 ) / 12;
      } else {
        if (isNylo) {
          adapterSeparation = ( ( endCapPipeSize.adapterOffset || 0 ) + ( endCapPipeSize.adapterC || 0 ) + 3.0 ) / 12;
        } else {
          adapterSeparation = ManifoldUtils.getManifoldStructureDimensions(structureType).wallThickness;
        }
      }

      const trunkSeparation = initialSeparation + adapterSeparation + pipeSeparation;

      return trunkSeparation;
    } catch (e) {
      console.error(`ManifoldUtils..getTrunkSeparation: ${e.message}`);
      return 0;
    }
  },

  getTrunkSeparationBySize: ( manifoldSizeByVolume, chamberType, separationDistance, isIsolator, preventCollapse = false ) => {
    let result = {
      inletTrunkSeparation: 0,
      outletTrunkSeparation: 0
    };

    const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();

    const allInletSizes = ManifoldUtils.getManifoldDatasetByChamberId(chamberType.id, manifoldSizeByVolume.position, manifoldSizeByVolume.inletType === "nyloplast");
    const thisInletSize = ManifoldUtils.getManifoldDatasetObject(allInletSizes, manifoldSizeByVolume.inletSize);

    let endCapPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === ( isIsolator ? thisInletSize.isolatorStubSize : thisInletSize.stubNominal )
    );
    let trunkPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === thisInletSize.trunkNominal
    );

    let isNylo = manifoldSizeByVolume.inletType === "nyloplast";
    let structureSize = thisInletSize[isNylo ? "nyloplastStructureSize" : "concreteStructureSize"];

    if (!structureSize) {
      structureSize = 48;
    }

    let initialSeparation = 1; // (12") //manifoldSizing.structureDistance;
    let adapterSeparation = 0;
    let pipeSeparation =
      structureSize / 12 / 2 +
      trunkPipeSize.pipeOD / 2 +
      initialSeparation +
      trunkPipeSize.stonePerimeter;

    if (manifoldSizeByVolume.inletStubsNumber < 2 && !preventCollapse) {
      pipeSeparation = ( separationDistance - 12 ) / 12;
    } else {
      if (isNylo) {
        adapterSeparation = ( ( endCapPipeSize.adapterOffset || 0 ) + ( endCapPipeSize.adapterC || 0 ) + 3.0 ) / 12;
      } else {
        adapterSeparation = ManifoldUtils.getManifoldStructureDimensions(manifoldSizeByVolume.inletType).wallThickness;
      }
    }

    result.inletTrunkSeparation = initialSeparation + adapterSeparation + pipeSeparation;

    const allOutletSizes = ManifoldUtils.getManifoldDatasetByChamberId(chamberType.id, manifoldSizeByVolume.position, manifoldSizeByVolume.outletType === "nyloplast");
    const thisOutletSize = ManifoldUtils.getManifoldDatasetObject(allOutletSizes, manifoldSizeByVolume.outletSize);

    endCapPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === thisOutletSize.stubNominal
    );
    trunkPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === thisOutletSize.trunkNominal
    );

    isNylo = manifoldSizeByVolume.outletType === "nyloplast";
    structureSize = thisOutletSize[isNylo ? "nyloplastStructureSize" : "concreteStructureSize"];

    if (!structureSize) {
      structureSize = 48;
    }

    initialSeparation = 1; // (12") //manifoldSizing.structureDistance;
    adapterSeparation = 0;
    pipeSeparation =
      structureSize / 12 / 2 +
      trunkPipeSize.pipeOD / 2 +
      initialSeparation +
      trunkPipeSize.stonePerimeter;

    if (manifoldSizeByVolume.outletStubsNumber < 2 && !preventCollapse) {
      pipeSeparation = ( separationDistance - 12 ) / 12;
    } else {
      if (isNylo) {
        adapterSeparation = ( ( endCapPipeSize.adapterOffset || 0 ) + ( endCapPipeSize.adapterC || 0 ) + 3.0 ) / 12;
      } else {
        adapterSeparation = ManifoldUtils.getManifoldStructureDimensions(manifoldSizeByVolume.outletType).wallThickness;
      }
    }

    result.outletTrunkSeparation = initialSeparation + adapterSeparation + pipeSeparation;

    return result;
  },

  /**
   * Returns the Array dataset from the manifolds array, filtered by low/high and 0 values
   * @returns {*} - Chamber Object
   * @param chamberId {string} - Chamber ID, obtained from main dataset
   * @param highOrLow {string} i.e 'low' || 'high'
   * @param isNylo
   */
  getManifoldDatasetByChamberId: ( chamberId, highOrLow = "low", isNylo ) => {
    const dsArray = dataset["by-chamber"][chamberId];
    const testValue = "0";
    if (highOrLow && highOrLow === "low") {
      return dsArray.filter(values => {
        // returning only LOW

        if (isNylo && values.nyloplastStructureSize === testValue) return false;
        return (
          values.size
          && values.lowTrunkMaxFlowRate !== testValue
          // && values.highTrunkMaxFlowRate !== testValue
        );
      });
    } else {
      return dsArray.filter(values => {
        // returning only HIGH
        if (isNylo && values.nyloplastStructureSize === testValue) return false;
        return (
          values.size
          && values.type === "high"
          && values.highTrunkMaxFlowRate !== testValue
        );
      });
    }
  },

  /**
   * Returns a clean object of the specified manifold by size
   * @param manifoldArray {[]}
   * @param size {string} - string that specifies the size as i.e. '6X6'
   * @returns {{}|null}
   */
  getManifoldDatasetObject: ( manifoldArray, size ) => {
    try {
      return manifoldArray.reduce(( filtered, option ) => {
        if (option.size === size) {
          const trunkStubArray = option.size.toLowerCase().split("x");
          let retObj = {
            size: option.size,
            ChamberTypeId: option.ChamberTypeId,
            type: option.type,
            isConcentric: ( option.isConcentric.toLowerCase() === "true" ),
            trunk: parseFloat(option.trunk),
            stub: parseFloat(option.stub),
            trunkNominal: parseFloat(trunkStubArray[0]),
            stubNominal: parseFloat(trunkStubArray[1]),
            lowTrunkMaxFlowRate: parseFloat(option.lowTrunkMaxFlowRate),
            highTrunkMaxFlowRate: parseFloat(option.highTrunkMaxFlowRate),
            highFlowRate: parseFloat(option.highFlowRate),
            lowFlowRate: parseFloat(option.lowFlowRate),
            nyloplastStructureSize: parseFloat(option.nyloplastStructureSize),
            concreteStructureSize: parseFloat(option.concreteStructureSize),
            isolatorStubSize: parseFloat(option.isolatorStubSize),
            forceManifoldSpacer: ( ( option.forceManifoldSpacer || "" ).toLowerCase() === "true" )
          };
          filtered.push(retObj);
        }
        return filtered;
      }, [])[0];
    } catch (error) {
      console.error(`ManifoldUtils.getManifoldDatasetObject()::size should be "nXn", is invalid or undefined`);
      return null;
    }
  },

  /**
   * Returns the manifold construct to be used on Rule sets
   * @param chamberTypeId
   * @param manifoldTypeId
   * @param storageVolume
   * @param isInner
   * @returns {{sizing: {stubSizeNominal: number, stubSize: *, isolatorStubSize: *, trunkSize: *, trunkSizeNominal: number, manifoldSizeName, potentialSizes: (*), structureSize: (*|number), defaultManifoldSize}, chamberTypeId, structureType, manifoldPosition: *, rules: {assembly, placement}, flowRates: {volumeFlowRateMinumum, trunkMaxFlowRate, volumeFlowRateMaximum, stubFlowRate}}}
   */
  getManifoldConstruct: ( chamberTypeId, manifoldTypeId, storageVolume = 0, isInner = false ) => {
    try {
      const innerDefault = isInner ? dataset["manifold-rules"][chamberTypeId].default : null;
      const sizing = ManifoldUtils.getManifoldSizingByChamberType(chamberTypeId, storageVolume);
      const isInlet = manifoldTypeId === INLET_MANIFOLD_ID;
      const structureType = ( isInner ? innerDefault : sizing )[isInlet ? "inletType" : "outletType"];
      const isNylo = structureType === ManifoldStructureType.Nyloplast;
      const manifoldArray = ManifoldUtils.getManifoldDatasetByChamberId(
        chamberTypeId,
        isInner
          ? innerDefault[isInlet ? "inletPosition" : "outletPosition"]
          : sizing.position,
        isNylo);
      let defaultManifoldSize;
      let structureSize;

      // CULTEC, for some reason, in cultec it thinks its an inner manifold
      // if (isInner) {
      //   defaultManifoldSize = ManifoldUtils.getManifoldDatasetObject(
      //     manifoldArray,
      //     innerDefault.manifoldSize);
      //   structureSize = innerDefault.structureSize;
      // } else {
        defaultManifoldSize = ManifoldUtils.getManifoldDatasetObject(
          manifoldArray,
          manifoldTypeId === INLET_MANIFOLD_ID
            ? sizing["inletSize"]
            : sizing["outletSize"]);
        structureSize = defaultManifoldSize[isNylo ? "nyloplastStructureSize" : "concreteStructureSize"];
      // }

      const isHighPosition = ( isInner ? innerDefault : sizing ).position === "high";
      const rules = getManifoldRulesByChamberType(chamberTypeId, structureSize);
      return {
        chamberTypeId: chamberTypeId,
        structureType: structureType,
        manifoldPosition: isInner
          ? innerDefault[isInlet ? "inletPosition" : "outletPosition"]
          : sizing.position,
        sizing: {
          potentialSizes: manifoldArray,
          defaultManifoldSize,
          manifoldSizeName: defaultManifoldSize.size,
          structureSize,
          isolatorStubSize: defaultManifoldSize.isolatorStubSize,
          stubSize: defaultManifoldSize.stub,
          stubSizeNominal: defaultManifoldSize.stubNominal,
          trunkSize: defaultManifoldSize.trunk,
          trunkSizeNominal: defaultManifoldSize.trunkNominal
        },
        flowRates: {
          volumeFlowRateMinumum: sizing.low,
          volumeFlowRateMaximum: sizing.high,
          stubFlowRate: defaultManifoldSize[isHighPosition ? "highFlowRate" : "lowFlowRate"],
          trunkMaxFlowRate: defaultManifoldSize[isHighPosition ? "highTrunkMaxFlowRate" : "lowTrunkMaxFlowRate"]
        },
        rules
      };

    } catch (error) {
      console.error(`ManifoldUtils.getManifoldConstruct() :: ${error.message}`);
    }


  }
};


export const getManifoldSeparationOffset = ( separationDistance, otherSeparationDistance ) => {
  if (separationDistance > 12) {
    return Math.max(otherSeparationDistance, separationDistance / 12);
  }
  return otherSeparationDistance;
};

export const getManifoldBendSeparation = ( manifold, chamberType, chamberRowSpacing, separationDistance, isIsolator ) => {
  const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();
  let endCapPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === ( isIsolator && manifold.isInlet ) ? manifold.manifoldSize.isolatorStubSize : manifold.manifoldSize.stubNominal
  );
  let trunkPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === manifold.manifoldSize.trunkNominal
  );

  if (trunkPipeSize.pipeSize !== endCapPipeSize.pipeSize) {
    return 0;
  }

  const initialSeparation = ( chamberType.width + chamberRowSpacing - endCapPipeSize.pipeOD ) / 2;
  const pipeSeparation = getManifoldSeparationOffset(separationDistance, ( trunkPipeSize.stonePerimeter || 0 ));
  return initialSeparation - pipeSeparation;
};

export const getManifoldBendSeparationBySize = ( manifoldSizeByVolume, chamberType, chamberRowSpacing, separationDistance, isIsolator ) => {
  let result = {
    inletBendSeparation: 0,
    outletBendSeparation: 0
  };

  const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();

  const allInletSizes = ManifoldUtils.getManifoldDatasetByChamberId(chamberType.id, manifoldSizeByVolume.position, manifoldSizeByVolume.inletType === "nyloplast");
  const thisInletSize = ManifoldUtils.getManifoldDatasetObject(allInletSizes, manifoldSizeByVolume.inletSize);

  let endCapPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === ( isIsolator ? thisInletSize.isolatorStubSize : thisInletSize.stubNominal )
  );
  let trunkPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === thisInletSize.trunkNominal
  );

  if (trunkPipeSize.pipeSize === endCapPipeSize.pipeSize) {
    const initialSeparation = ( chamberType.width + chamberRowSpacing - endCapPipeSize.pipeOD ) / 2;
    const pipeSeparation = getManifoldSeparationOffset(separationDistance, ( trunkPipeSize.stonePerimeter || 0 ));
    result.inletBendSeparation = initialSeparation - pipeSeparation;
  }

  const allOutletSizes = ManifoldUtils.getManifoldDatasetByChamberId(chamberType.id, manifoldSizeByVolume.position, manifoldSizeByVolume.outletType === "nyloplast");
  const thisOutletSize = ManifoldUtils.getManifoldDatasetObject(allOutletSizes, manifoldSizeByVolume.outletSize);

  endCapPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === thisOutletSize.stubNominal
  );
  trunkPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === thisOutletSize.trunkNominal
  );

  if (trunkPipeSize.pipeSize === endCapPipeSize.pipeSize) {
    const initialSeparation = ( chamberType.width + chamberRowSpacing - endCapPipeSize.pipeOD ) / 2;
    const pipeSeparation = getManifoldSeparationOffset(separationDistance, ( trunkPipeSize.stonePerimeter || 0 ));
    result.outletBendSeparation = initialSeparation - pipeSeparation;
  }

  return result;
};

export const getManifoldEndCapSeparation = ( manifold, separationDistance, isIsolator ) => {
  const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();
  let endCapPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === ( isIsolator && manifold.isInlet ) ? manifold.manifoldSize.isolatorStubSize : manifold.manifoldSize.stubNominal
  );
  let trunkPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === manifold.manifoldSize.trunkNominal
  );

  if (trunkPipeSize.pipeSize === endCapPipeSize.pipeSize) {
    return 0;
  }

  let initialSeparation = ( trunkPipeSize.pitch || 0 ) * 3;
  let pipeSeparation = getManifoldSeparationOffset(separationDistance, trunkPipeSize.stonePerimeter);

  return initialSeparation + pipeSeparation;
};

export const getManifoldEndCapSeparationBySize = ( manifoldSizeByVolume, chamberType, separationDistance, isIsolator ) => {
  let result = {
    inletEndCapSeparation: 0,
    outletEndCapSeparation: 0
  };

  const manifoldPipeSizes = ManifoldUtils.getAllManifoldPipeSizeData();

  const allInletSizes = ManifoldUtils.getManifoldDatasetByChamberId(chamberType.id, manifoldSizeByVolume.position, manifoldSizeByVolume.inletType === "nyloplast");
  const thisInletSize = ManifoldUtils.getManifoldDatasetObject(allInletSizes, manifoldSizeByVolume.inletSize);

  let endCapPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === ( isIsolator ? thisInletSize.isolatorStubSize : thisInletSize.stubNominal )
  );
  let trunkPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === thisInletSize.trunkNominal
  );

  if (trunkPipeSize.pipeSize !== endCapPipeSize.pipeSize) {
    let initialSeparation = ( trunkPipeSize.pitch || 0 ) * 3;
    let pipeSeparation = getManifoldSeparationOffset(separationDistance, trunkPipeSize.stonePerimeter);
    result.inletEndCapSeparation = initialSeparation + pipeSeparation;
  }

  const allOutletSizes = ManifoldUtils.getManifoldDatasetByChamberId(chamberType.id, manifoldSizeByVolume.position, manifoldSizeByVolume.outletType === "nyloplast");
  const thisOutletSize = ManifoldUtils.getManifoldDatasetObject(allOutletSizes, manifoldSizeByVolume.outletSize);

  endCapPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === thisOutletSize.stubNominal
  );
  trunkPipeSize = manifoldPipeSizes.find(
    size => size.pipeSize === thisOutletSize.trunkNominal
  );

  if (trunkPipeSize.pipeSize !== endCapPipeSize.pipeSize) {
    let initialSeparation = ( trunkPipeSize.pitch || 0 ) * 3;
    let pipeSeparation = getManifoldSeparationOffset(separationDistance, trunkPipeSize.stonePerimeter);
    result.outletEndCapSeparation = initialSeparation + pipeSeparation;
  }

  return result;
};

export const getStructureTypesByVolume = ( storageVolume, chamberType ) => {
  let inletStructureType = "round";
  let outletStructureType = "round";

  let manifoldSizing = ManifoldUtils.getManifoldSizingByChamberType(chamberType.id, storageVolume);

  if (manifoldSizing && manifoldSizing.inletType) {
    inletStructureType = manifoldSizing.inletType;
  }
  if (manifoldSizing && manifoldSizing.outletType) {
    outletStructureType = manifoldSizing.outletType;
  }

  return { inletStructureType, outletStructureType };
};

/**
 * Given a chamber type and structure size, returns the manifold rules that should be applied for internal manifolds only.
 * @param {string} chamberTypeId The ID of the chamber type
 * @param {number} structureSize The size of the structure that will/does exist on this manifold
 */
export const getManifoldRulesByChamberType = ( chamberTypeId, structureSize ) => {
  const manifoldRules = dataset["manifold-rules"][chamberTypeId];
  const assembly = manifoldRules.assembly.find(asm => asm.structureSize === structureSize);
  const placement = manifoldRules.placement
    .sort(( a, b ) => b.structureSizeMax - a.structureSizeMax)
    .find(x => x.structureSizeMin <= structureSize);
  return {
    assembly,
    placement
  };
};



// WEBPACK FOOTER //
// ./src/lib/manifolds/utils.js